import { Currency, CurrencyAmount } from '@kodiak-finance/sdk-core'
import IUniswapV2PairJson from '@kodiak-finance/v2-core/build/IUniswapV2Pair.json'
import { computePairAddress, Pair } from '@kodiak-finance/v2-sdk'
import { useMemo } from 'react'
import { Abi, Address } from 'viem'

import { V2_FACTORY_ADDRESSES } from '../constants/addresses'
import { useReadContractMultiple } from './useReadContractMultiple'

const { abi: IUniswapV2PairABI } = IUniswapV2PairJson

export enum PairState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID,
}

export function useV2Pairs(currencies: [Currency | undefined, Currency | undefined][]): [PairState, Pair | null][] {
  const tokens = useMemo(
    () => currencies.map(([currencyA, currencyB]) => [currencyA?.wrapped, currencyB?.wrapped]),
    [currencies]
  )

  const pairAddresses = useMemo(
    () =>
      tokens.map(([tokenA, tokenB]) => {
        return tokenA &&
          tokenB &&
          tokenA.chainId === tokenB.chainId &&
          !tokenA.equals(tokenB) &&
          V2_FACTORY_ADDRESSES[tokenA.chainId]
          ? computePairAddress({ factoryAddress: V2_FACTORY_ADDRESSES[tokenA.chainId], tokenA, tokenB })
          : undefined
      }),
    [tokens]
  )

  const contracts = useMemo(() => {
    return pairAddresses
      .filter((address): address is Address => address !== undefined)
      .map((address) => ({
        address, // Type is now guaranteed to be Address
        abi: IUniswapV2PairABI as Abi,
        functionName: 'getReserves' as const,
      }))
  }, [pairAddresses])

  const { data: results, isLoading } = useReadContractMultiple({
    contracts,
    query: {
      enabled: contracts.length > 0, // Simplified check since all contracts now have valid addresses
    },
  })

  return useMemo(() => {
    let resultIndex = 0
    return tokens.map(([tokenA, tokenB], i) => {
      const pairAddress = pairAddresses[i]
      if (!pairAddress) {
        if (!tokenA || !tokenB || tokenA.equals(tokenB)) return [PairState.INVALID, null]
        return [PairState.NOT_EXISTS, null]
      }
      const result = results?.[resultIndex]
      const loading = isLoading || result === undefined
      const reserves = result?.result as [bigint, bigint, number] | undefined
      // Increment the index for the next valid address
      resultIndex++

      if (loading) return [PairState.LOADING, null]
      if (!tokenA || !tokenB || tokenA.equals(tokenB)) return [PairState.INVALID, null]
      if (!reserves || result?.status === 'failure') return [PairState.NOT_EXISTS, null]
      const [reserve0, reserve1] = reserves
      const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
      return [
        PairState.EXISTS,
        new Pair(
          CurrencyAmount.fromRawAmount(token0, reserve0.toString()),
          CurrencyAmount.fromRawAmount(token1, reserve1.toString())
        ),
      ]
    })
  }, [results, tokens, isLoading, pairAddresses])
}

export function useV2Pair(tokenA?: Currency, tokenB?: Currency): [PairState, Pair | null] {
  const inputs: [[Currency | undefined, Currency | undefined]] = useMemo(() => [[tokenA, tokenB]], [tokenA, tokenB])
  return useV2Pairs(inputs)[0]
}
